import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Chip,
  IconButton,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";

import { APP_API_V1_BASE } from "../../../config/api";
const MAX_FILE_SIZE_MB = 50;
const ALLOWED_EXTENSIONS = [".bin", ".hex", ".img"];
const DEVICE_TYPE_API = `${APP_API_V1_BASE}/device-types`;

export default function FirmwarePackageUploadDialog({
  open,
  onClose,
  onUploadStart, // (fileName: string) => void
  onUploadProgress, // (percent: number) => void
  onUploadComplete, // () => void
  onCloseProgress,
}) {
  const inputRef = useRef(null);
  const [title, setTitle] = useState("");
  const [bumpType, setBumpType] = useState("PATCH");
  const [deviceVersionTag, setDeviceVersionTag] = useState("");
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [deviceTypeId, setDeviceTypeId] = useState("");
  const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);
  const [file, setFile] = useState(null);
  const [releaseNotes, setReleaseNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [resettingVersion, setResettingVersion] = useState(false);
  const [error, setError] = useState("");
  const [versionResetNotice, setVersionResetNotice] = useState("");
  const [fileError, setFileError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const { permissions, activeOrg } = useAuth();
  const canUpload = can(permissions, "firmware.upload");
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    if (!open) return;
    setLoadingDeviceTypes(true);
    axios
      .get(DEVICE_TYPE_API, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      })
      .then((res) => setDeviceTypes(res.data || []))
      .catch(() => setError("Failed to load device profiles."))
      .finally(() => setLoadingDeviceTypes(false));
  }, [open, token, activeOrg?.id]);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setBumpType("PATCH");
      setDeviceVersionTag("");
      setDeviceTypeId("");
      setFile(null);
      setReleaseNotes("");
      // setError("");
      setVersionResetNotice("");
      setFileError("");
      setFieldErrors({});
      setLoading(false);
      setResettingVersion(false);
      setDragOver(false);
      setDeviceTypes([]);
    }
  }, [open]);

  const validateFile = (f) => {
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) return `Invalid file type (${ext})`;
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024)
      return `File exceeds ${MAX_FILE_SIZE_MB} MB`;
    return null;
  };

  const handleFileSelect = (f) => {
    if (!f) return;
    const err = validateFile(f);
    if (err) {
      setFile(null);
      setFileError(err);
      return;
    }
    setFile(f);
    setFileError("");
    setFieldErrors((prev) => ({ ...prev, file: "" }));
  };

  const submit = async () => {
      let errors = {};
    if (!title.trim()) errors.title = "Required"; 
    if (!deviceTypeId) errors.deviceTypeId = "Required"; 
    if (!file) errors.file = "Package file is required"; 

    if (Object.keys(errors).length > 0) { 
      setFieldErrors(errors); 
      return;
    }

    setFieldErrors({});

    setLoading(true);
    setError("");

    // ── Notify parent: upload is starting ──────────────────────────
    onUploadStart?.(file.name);

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("name", title.trim());
      form.append("bump_type", bumpType);
      form.append("device_type_id", deviceTypeId);
      if (activeOrg?.id) {
        form.append("org_id", activeOrg.id);
      }
      if (deviceVersionTag.trim()) {
        form.append("device_version_tag", deviceVersionTag.trim());
      }
      if (releaseNotes.trim())
        form.append("release_notes", releaseNotes.trim());
      await axios.post(`${APP_API_V1_BASE}/firmware/packages`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
        timeout: 30000,
        // ── Stream progress back to parent ──────────────────────
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onUploadProgress?.(percent);
          }
        },
      });

      // ── Notify parent: upload finished successfully ─────────────
      onUploadComplete?.();
    } catch (err) {
      // Reset progress overlay on failure
      onUploadProgress?.(0);
      const apiError = axios.isAxiosError(err) ? err.response?.data : null;
      const errorMessage =
        apiError?.error || apiError?.message || "Upload failed";
      const errorDetails = apiError?.details
        ? ` (${String(apiError.details)})`
        : "";
      setError(
        axios.isAxiosError(err)
          ? `${errorMessage}${errorDetails}`
          : "Unexpected error occurred",
      );
      onCloseProgress();
      setLoading(false);
    }
    // Note: setLoading(false) is intentionally NOT called on success —
    // the parent's onUploadComplete drives the close + refresh flow.
  };

  const resetVersionSeries = async () => {
    if (!deviceTypeId || resettingVersion || loading) return;

    const proceed = window.confirm(
      "Reset firmware version series for this device profile? The next package version will start at 1.0.0.",
    );
    if (!proceed) return;

    setResettingVersion(true);
    setError("");
    setVersionResetNotice("");

    try {
      const response = await axios.post(
        `${APP_API_V1_BASE}/firmware/packages/device-types/${deviceTypeId}/version/reset`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
        },
      );

      const nextVersion = response?.data?.next_version || "1.0.0";
      setVersionResetNotice(
        `Version series reset successfully. Next package will start at ${nextVersion}.`,
      );
    } catch (err) {
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.error ||
              err.response?.data?.message ||
              "Version reset failed"
          : "Version reset failed",
      );
    } finally {
      setResettingVersion(false);
    }
  };

  if (!canUpload) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Access Restricted</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            You do not have permission to upload firmware packages.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden",
          flex: "0 0 auto",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          fontSize: 17,
          px: 3.2,
          py: 2.8,

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",

          color: "#fff",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
        }}
      >
        Add Package
        <IconButton
          onClick={onClose}
          sx={{
            color: "#fff",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3.2, py: 2.5, background: "#FFFFFF" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {versionResetNotice && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {versionResetNotice}
          </Alert>
        )}

        {/* Title + Version Strategy */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
          gap={2}
          mb={1}
        >
          <TextField
            label="Title *"
            value={title}
           onChange={(e) => {
              setTitle(e.target.value);
              setFieldErrors((p) => ({ ...p, title: "" })); 
            }}
            error={!!fieldErrors.title} 
            helperText={fieldErrors.title}
            disabled={loading}
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
          <TextField
            select
            label="Version bump"
            value={bumpType}
            onChange={(e) => setBumpType(e.target.value)}
            disabled={loading}
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          >
            <MenuItem value="PATCH">Patch (x.y.z+1)</MenuItem>
            <MenuItem value="MINOR">Minor (x.y+1.0)</MenuItem>
            <MenuItem value="MAJOR">Major (x+1.0.0)</MenuItem>
          </TextField>
        </Box>

        <Alert severity="info" sx={{ mb: 1.5 }}>
          Version is generated by the server automatically. After reset, the
          first upload always starts at 1.0.0.
        </Alert>

        <TextField
          label="Device-reported version tag"
          helperText="Optional label reported by firmware runtime"
          fullWidth
          size="small"
          margin="dense"
          value={deviceVersionTag}
          onChange={(e) => setDeviceVersionTag(e.target.value)}
          disabled={loading}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />

        <TextField
          select
          label="Device profile *"
          fullWidth
          size="small"
          margin="dense"
          value={deviceTypeId}
          onChange={(e) => {
            setDeviceTypeId(e.target.value);
            setFieldErrors((p) => ({ ...p, deviceTypeId: "" })); 
          }}
          error={!!fieldErrors.deviceTypeId} 
          helperText={fieldErrors.deviceTypeId}
          disabled={loading || loadingDeviceTypes}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        >
          {loadingDeviceTypes ? (
            <MenuItem disabled>Loading…</MenuItem>
          ) : deviceTypes.length === 0 ? (
            <MenuItem disabled>No device profiles available</MenuItem>
          ) : (
            deviceTypes.map((dt) => (
              <MenuItem key={dt.id} value={dt.id}>
                {dt.name}
              </MenuItem>
            ))
          )}
        </TextField>

        <Box mt={1.5} mb={0.5}>
          <Button
            variant="outlined"
            color="warning"
            onClick={resetVersionSeries}
            disabled={!deviceTypeId || loading || resettingVersion}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            {resettingVersion
              ? "Resetting..."
              : "Reset version series (next 1.0.0)"}
          </Button>
        </Box>

        <Divider sx={{ my: 2.3 }} />

        <Typography fontWeight={600} fontSize={14} mb={1}>
          Package type
        </Typography>
        <RadioGroup row value="binary">
          <FormControlLabel
            value="binary"
            control={<Radio size="small" />}
            label={<Typography fontSize={14}>Upload binary file</Typography>}
          />
        </RadioGroup>

        {/* Drop zone */}
        <Box mt={1.5}>
          <Typography fontWeight={600} fontSize={14} mb={1}>
            Package file *
          </Typography>
          <Box
            onClick={() => !loading && inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (!loading) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (!loading) handleFileSelect(e.dataTransfer.files?.[0]);
            }}
            sx={{
              border: "2px dashed",
              borderColor: fileError || fieldErrors.file ? "error.main" : "#ccc",
              borderRadius: 2,
              p: 3.4,
              textAlign: "center",
              cursor: loading ? "not-allowed" : "pointer",
              background: dragOver ? "#EEF5FF" : file ? "#EFFAF3" : "#FAFCFF",
              transition: "all 0.2s",
            }}
          >
            <CloudUpload
              sx={{
                fontSize: 32,
                color: file ? "success.main" : "text.disabled",
                mb: 0.5,
              }}
            />
            {file ? (
              <Box>
                <Typography fontSize={14} fontWeight={600} color="success.main">
                  {file.name}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
            ) : (
              <Typography fontSize={14} color="text.secondary">
                Drag and drop a package file or{" "}
                <Typography
                  component="span"
                  color="primary"
                  fontWeight={600}
                  sx={{ textDecoration: "underline" }}
                >
                  Browse file
                </Typography>
              </Typography>
            )}
            <input
              ref={inputRef}
              type="file"
              hidden
              disabled={loading}
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
            />
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={1}
            mt={1}
            flexWrap="wrap"
          >
            {fieldErrors.file ? (
              <Typography fontSize={13} color="error">
                {fieldErrors.file}
              </Typography>
            ) : (
              <>
                {ALLOWED_EXTENSIONS.map((ext) => (
                  <Chip
                    key={ext}
                    label={ext}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 11, height: 22 }}
                  />
                ))}
                <Typography fontSize={12} color="text.disabled">
                  · Max {MAX_FILE_SIZE_MB} MB
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <TextField
          label="Release notes"
          fullWidth
          multiline
          rows={3}
          margin="dense"
          size="small"
          value={releaseNotes}
          onChange={(e) => setReleaseNotes(e.target.value)}
          disabled={loading}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3.2,
          py: 2.2,
          gap: 1.2,
          borderTop: "1px solid #E8ECF2",
          background: "#FFFFFF",
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          color="inherit"
          variant="outlined"
          sx={{
            borderRadius: "10px",
            px: 2.6,
            textTransform: "uppercase",
            fontWeight: 700,
            color: "#1976d2",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={loading || loadingDeviceTypes || resettingVersion}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{
            borderRadius: "10px",
            px: 3.2,
            textTransform: "uppercase",
            fontWeight: 800,
            background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
            "&:hover": {
              background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
            },
          }}
        >
          {loading ? "Uploading…" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
